/**
 * Deals as a table.
 *
 * The board answers "where is everything in the pipeline"; this answers "who is
 * working what, and what is it worth". Same data, same saved views — a saved
 * view's `viewType` decides which one opens.
 */

import { Form, Link, useLoaderData, useNavigation, useSearchParams } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import type { DealListResult } from '@erp/contracts'
import type { Organization } from '@erp/contracts'
import type { FieldRegistry, SavedView } from '@erp/contracts'
import { api } from '../api'
import { usePagedRows } from '../paging'
import { useAssignees } from '../use-assignees'
import { filtersParam } from '../view-state'
import { ViewBar, handleViewIntent } from '../components/view-bar'
import { formatDate, formatMoney } from '../format'
import { useDealStatus, useUser, useUsers } from '../status-context'
import { Badge, Button, EmptyState, PageHeader, SearchInput, Spinner, Table, Toolbar } from '../ui'

interface ListData {
  page: DealListResult
  registry: FieldRegistry
  views: SavedView[]
  /** Deals reference an organization by id; the table needs its name. */
  organizations: Organization[]
}

function fetchPage(request: Request): Promise<DealListResult> {
  const url = new URL(request.url)
  return api.deals.list({
    search: url.searchParams.get('q') ?? undefined,
    state: (url.searchParams.get('state') as 'open' | 'won' | 'lost' | 'all' | null) ?? 'open',
    filters: filtersParam(url.searchParams),
    cursor: url.searchParams.get('cursor') ?? undefined,
    sort: 'updatedAt',
    direction: 'desc',
    limit: 50,
  })
}

/** Fetcher-only route: just the page, which is what usePagedRows expects. */
export const pageLoader = ({ request }: LoaderFunctionArgs) => fetchPage(request)

export async function loader({ request }: LoaderFunctionArgs): Promise<ListData> {
  const [page, registry, views, organizations] = await Promise.all([
    fetchPage(request),
    api.registry('deal'),
    api.views.forEntity('deal'),
    // Capped at 200, the same ceiling as the other pickers; past that this wants
    // the organization name denormalised onto the list query instead.
    api.organizations.list({ limit: 200, sort: 'name', direction: 'asc' }),
  ])
  return { page, registry, views, organizations: organizations.rows }
}

export async function action({ request }: ActionFunctionArgs) {
  const form = await request.formData()
  const result = await handleViewIntent(String(form.get('intent')), form)
  return result ?? { ok: false, message: 'Unknown action' }
}

export default function DealsList() {
  const { page, registry, views, organizations } = useLoaderData() as ListData
  const navigation = useNavigation()
  const [searchParams] = useSearchParams()
  const { rows, cursor, loading, loadMore } = usePagedRows(page, '/crm/deals/list/page')
  const assignees = useAssignees(
    'deal',
    rows.map((deal) => deal.id),
  )

  const organizationName = new Map(organizations.map((o) => [o.id, o.name]))
  const state = searchParams.get('state') ?? 'open'

  return (
    <>
      <PageHeader
        title="Deals"
        actions={
          <>
            <Link className="ax-btn" to={{ pathname: '/crm/deals', search: searchParams.toString() }}>
              Board
            </Link>
            <Link to="/crm/deals/new" className="ax-btn ax-btn-primary">
              Create
            </Link>
          </>
        }
      />

      <ViewBar entity="deal" registry={registry} views={views} />

      <Toolbar>
        <Form method="get" role="search">
          <SearchInput
            name="q"
            defaultValue={searchParams.get('q') ?? ''}
            placeholder="Search by organization"
          />
          <select className="ax-select" name="state" defaultValue={state}>
            <option value="open">Open</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
            <option value="all">All</option>
          </select>
          <Button type="submit">Apply</Button>
        </Form>
        <span className="ax-toolbar-count">
          {page.totalCount} {page.totalCount === 1 ? 'deal' : 'deals'}
        </span>
        {navigation.state === 'loading' ? <Spinner /> : null}
      </Toolbar>

      {rows.length === 0 ? (
        <EmptyState title="No deals match this view" />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th scope="col">Organization</th>
                <th scope="col">Amount</th>
                <th scope="col">Stage</th>
                <th scope="col">Owner</th>
                <th scope="col">Working on it</th>
                <th scope="col">Expected close</th>
                <th scope="col">Last modified</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((deal) => (
                <tr key={deal.id}>
                  <td>
                    <Link to={`/crm/deals/${deal.id}`}>
                      {(deal.organizationId ? organizationName.get(deal.organizationId) : null) ??
                        'Unlinked deal'}
                    </Link>
                  </td>
                  <td>{deal.amount !== null ? formatMoney(deal.amount, deal.currency) : '—'}</td>
                  <td>
                    <StageCell statusId={deal.statusId} />
                  </td>
                  <td>
                    <OwnerCell ownerId={deal.ownerId} />
                  </td>
                  <td>
                    <AssigneeCell userIds={assignees.get(deal.id) ?? []} />
                  </td>
                  <td>{deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : '—'}</td>
                  <td>{formatDate(deal.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {cursor ? (
            <div className="ax-list-footer">
              <Button onClick={loadMore} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  )
}

function StageCell({ statusId }: { statusId: string }) {
  const status = useDealStatus(statusId)
  return <Badge color={status?.color ?? 'gray'}>{status?.name ?? '—'}</Badge>
}

/** Ownership: single, and the answer to "whose deal is this". */
function OwnerCell({ ownerId }: { ownerId: string | null }) {
  const owner = useUser(ownerId)
  if (ownerId === null) return <span className="ax-text-muted">Unassigned</span>
  return <span>{owner?.name ?? 'Unknown'}</span>
}

/** Assignment: many, and the answer to "who is actually working it". */
function AssigneeCell({ userIds }: { userIds: readonly string[] }) {
  const users = useUsers()
  if (userIds.length === 0) return <span className="ax-text-muted">—</span>
  const names = userIds.map((id) => users.find((user) => user.id === id)?.name ?? 'Unknown')
  return <span>{names.join(', ')}</span>
}
